import { useState, FC } from 'react';
import Alert from '@mui/material/Alert';
import { useSnackbar } from 'notistack';
import { Theme } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { Box, Fade, ButtonGroup, Button, Typography } from '@mui/material';
import { Close, Restore } from '@mui/icons-material';
import { Modal, Loader } from '../../../components';
import { resetSpec } from '../../../fetch';
import { IBuild } from '../../../models';

interface IResetSpecModal {
  currentSpec: IBuild;
  open: boolean;
  onClose: () => void;
  fetchBuilds: () => void;
}

export const ResetSpecModal: FC<IResetSpecModal> = ({
  open,
  onClose,
  currentSpec,
  fetchBuilds
}) => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [isResetting, setResetting] = useState(false);
  return (
    <Modal open={open} onClose={onClose} maxWidth="sm">
      <Fade in={open}>
        <div>
          {isResetting && (
            <Loader position="centered" type="overlay">
              Resetting...
            </Loader>
          )}
          <Box marginBottom="1rem">
            {currentSpec && (
              <Typography variant="h5">
                Reset Spec {currentSpec.neighborhood} - Lot {currentSpec.lotNumber}{' '}
                {currentSpec && currentSpec.status ? `- ${currentSpec.status}` : ''}{' '}
                {currentSpec && currentSpec.buyer ? `- ${currentSpec.buyer}` : ''}
              </Typography>
            )}
          </Box>
          <Alert severity="warning">
            This function will reset a spec to put it back on the Spec list. This will delete any
            purchaser specific documents tied to the spec.
          </Alert>
          <Box marginTop="1rem">
            <ButtonGroup>
              <Button
                id="reset"
                className={classes.resetButton}
                variant="contained"
                disabled={isResetting}
                startIcon={<Restore />}
                onClick={async () => {
                  setResetting(true);
                  try {
                    await resetSpec(currentSpec.buildId);
                    enqueueSnackbar(`Spec reset!`, {
                      variant: 'success'
                    });
                    onClose();
                    fetchBuilds();
                  } catch (error) {
                    enqueueSnackbar('Error resetting spec, please try again.', {
                      variant: 'error'
                    });
                  } finally {
                    setResetting(false);
                  }
                }}
              >
                Reset
              </Button>
              <Button
                color="inherit"
                id="cancel"
                variant="contained"
                startIcon={<Close />}
                onClick={onClose}
              >
                Cancel
              </Button>
            </ButtonGroup>
          </Box>
        </div>
      </Fade>
    </Modal>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  saveWrapper: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(1)
  },
  resetButton: {
    backgroundColor: theme.palette.warning.main,
    color: '#fff'
  }
}));
